import React, { useState, useEffect } from 'react';
import { Shield, Info, Lock, Users, CheckSquare, Square } from 'lucide-react';
import Input from '../../components/forms/Input';
import Button from '../../components/ui/Button';
import apiService from '../../services/apiService';

const RoleForm = ({ role, onSubmit, onCancel, isLoading, isEdit = false }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        activo: 1,
        necesita_almacen: 0,
        permisos: []
    });

    const [errors, setErrors] = useState({});
    const [permisosDisponibles, setPermisosDisponibles] = useState([]);
    const [loadingPermisos, setLoadingPermisos] = useState(true);

    // Cargar permisos desde la API
    useEffect(() => {
        loadPermisos();
    }, []);

    const loadPermisos = async () => {
        try {
            setLoadingPermisos(true);
            const result = await apiService.getPermisos();

            if (result.success) {
                // Organizar permisos por módulos
                const permisosOrganizados = Object.keys(result.data).map(modulo => ({
                    modulo: modulo.charAt(0).toUpperCase() + modulo.slice(1),
                    icon: getModuloIcon(modulo),
                    permisos: result.data[modulo].map(p => ({
                        id: p.nombre,
                        nombre: p.descripcion || p.nombre,
                        descripcion: p.descripcion || `Permiso para ${modulo}`
                    }))
                }));
                setPermisosDisponibles(permisosOrganizados);
            }
        } catch (error) {
            console.error('Error al cargar permisos:', error);
        } finally {
            setLoadingPermisos(false);
        }
    };

    const getModuloIcon = (modulo) => {
        const icons = {
            'usuarios': <Users size={16} />,
            'roles': <Shield size={16} />,
            'productos': <Lock size={16} />,
            'ventas': <Lock size={16} />,
            'reportes': <Lock size={16} />,
            'clientes': <Users size={16} />,
            'configuracion': <Lock size={16} />,
            'almacenes': <Lock size={16} />,
            'catalogos': <Lock size={16} />,
            'flete': <Lock size={16} />
        };
        return icons[modulo.toLowerCase()] || <Lock size={16} />;
    };

    // Cargar datos del rol si estamos editando
    useEffect(() => {
        if (isEdit && role) {
            setFormData({
                nombre: role.nombre || '',
                descripcion: role.descripcion || '',
                activo: role.activo || 1,
                necesita_almacen: role.necesita_almacen ? 1 : 0,
                permisos: role.permisos || []
            });
        }
    }, [isEdit, role]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo cuando el usuario empiece a escribir
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handlePermisoToggle = (permisoId) => {
        setFormData(prev => ({
            ...prev,
            permisos: prev.permisos.includes(permisoId)
                ? prev.permisos.filter(id => id !== permisoId)
                : [...prev.permisos, permisoId]
        }));
    };

    const toggleModuloCompleto = (modulo) => {
        const permisosModulo = modulo.permisos.map(p => p.id);
        const todosMarcados = permisosModulo.every(id => formData.permisos.includes(id));

        if (todosMarcados) {
            // Desmarcamos todos los permisos del módulo
            setFormData(prev => ({
                ...prev,
                permisos: prev.permisos.filter(id => !permisosModulo.includes(id))
            }));
        } else {
            // Marcamos todos los permisos del módulo
            setFormData(prev => ({
                ...prev,
                permisos: [...new Set([...prev.permisos, ...permisosModulo])]
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validar nombre
        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre del rol es requerido';
        } else if (formData.nombre.length < 2) {
            newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
        }


        // Validar que tenga al menos un permiso
        if (formData.permisos.length === 0) {
            newErrors.permisos = 'Debe seleccionar al menos un permiso';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Preparar datos para enviar
        const dataToSubmit = {
            nombre: formData.nombre.trim(),
            descripcion: formData.descripcion.trim(),
            activo: parseInt(formData.activo),
            necesita_almacen: parseInt(formData.necesita_almacen),
            permisos: formData.permisos
        };

        onSubmit(dataToSubmit);
    };

    return (
        <form onSubmit={handleSubmit} className="role-form">
            <div className="form-container">
                {/* Columna Izquierda - Información Básica */}
                <div className="form-column">
                    <h3 className="column-title">
                        <Info size={18} />
                        Información del Rol
                    </h3>

                    {/* Nombre del rol */}
                    <div className="form-group">
                        <Input
                            label="Nombre del Rol"
                            type="text"
                            value={formData.nombre}
                            onChange={(e) => handleInputChange('nombre', e.target.value)}
                            error={errors.nombre}
                            icon={<Shield size={18} />}
                            placeholder="Ej: Administrador"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Descripción */}
                    <div className="form-group">
                        <label className="form-label">Descripción</label>
                        <textarea
                            value={formData.descripcion}
                            onChange={(e) => handleInputChange('descripcion', e.target.value)}
                            className={`form-textarea ${errors.descripcion ? 'error' : ''}`}
                            placeholder="Describe las responsabilidades de este rol..."
                            rows={4}
                            disabled={isLoading}
                        />
                        {errors.descripcion && (
                            <span className="error-message">{errors.descripcion}</span>
                        )}
                    </div>

                    {/* Necesita Almacén */}
                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData.necesita_almacen === 1}
                                onChange={(e) => handleInputChange('necesita_almacen', e.target.checked ? 1 : 0)}
                                disabled={isLoading}
                                className="form-checkbox"
                            />
                            <div className="checkbox-content">
                                <span className="checkbox-title">Necesita Almacén</span>
                                <span className="checkbox-description">
                                    Los usuarios con este rol deben tener almenos un almacén asignado
                                </span>
                            </div>
                        </label>
                    </div>

                    {/* Estado (solo para edición) */}
                    {isEdit && (
                        <div className="form-group">
                            <label className="form-label">Estado del Rol</label>
                            <select
                                value={formData.activo}
                                onChange={(e) => handleInputChange('activo', e.target.value)}
                                className="form-select"
                                disabled={isLoading}
                            >
                                <option value={1}>✅ Activo</option>
                                <option value={0}>❌ Inactivo</option>
                            </select>
                            <small className="form-help">
                                Los roles inactivos no pueden ser asignados a usuarios
                            </small>
                        </div>
                    )}
                </div>

                {/* Columna Derecha - Permisos */}
                <div className="form-column">
                    <h3 className="column-title">
                        <Lock size={18} />
                        Permisos del Sistema
                    </h3>

                    {errors.permisos && (
                        <div className="error-message">{errors.permisos}</div>
                    )}

                    {loadingPermisos ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Cargando permisos...</p>
                        </div>
                    ) : (
                        <div className="permissions-container">
                        {permisosDisponibles.map((modulo) => {
                            const permisosModulo = modulo.permisos.map(p => p.id);
                            const todosMarcados = permisosModulo.every(id => formData.permisos.includes(id));
                            const algunosMarcados = permisosModulo.some(id => formData.permisos.includes(id));

                            return (
                                <div key={modulo.modulo} className="permissions-module">
                                    <div
                                        className="module-header"
                                        onClick={() => toggleModuloCompleto(modulo)}
                                    >
                                        <div className="module-info">
                                            {modulo.icon}
                                            <span className="module-name">{modulo.modulo}</span>
                                        </div>
                                        <div className="module-toggle">
                                            {todosMarcados ?
                                                <CheckSquare size={18} className="checked" /> :
                                                algunosMarcados ?
                                                    <Square size={18} className="partial" /> :
                                                    <Square size={18} />
                                            }
                                        </div>
                                    </div>

                                    <div className="module-permissions">
                                        {modulo.permisos.map((permiso) => (
                                            <div key={permiso.id} className="permission-item">
                                                <label className="permission-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.permisos.includes(permiso.id)}
                                                        onChange={() => handlePermisoToggle(permiso.id)}
                                                        disabled={isLoading}
                                                    />
                                                    <div className="permission-content">
                                                        <span className="permission-name">{permiso.nombre}</span>
                                                        <span className="permission-desc">{permiso.descripcion}</span>
                                                    </div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        </div>
                    )}

                    {!loadingPermisos && (
                        <div className="permissions-summary">
                            <strong>Total de permisos seleccionados: {formData.permisos.length}</strong>
                        </div>
                    )}
                </div>
            </div>

            {/* Botones de acción */}
            <div className="form-actions">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>

                <Button
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading}
                >
                    {isEdit ? 'Actualizar Rol' : 'Crear Rol'}
                </Button>
            </div>
        </form>
    );
};

export default RoleForm;